// See: backend/docs/arvento-telemetry.md §20
// Uyarı Merkezi — Araç İzle (/live) altında, Telemetri'nin hemen altındaki sekme (telemetryAlerts).
// 3 alt sekme: (1) Km karşılaştırması, (2) Servis dışı kullanım, (3) Hız uyarıları.
// Servissiz/hız sekmeleri: filo günlük çubuk grafik (son 30g) + araç bazlı bugün/3g/7g/30g özeti + accordion.
// Canlı "servis dışı" rozeti burada yok; Telemetri sekmesinde durur.

import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { AuthContext } from "shared/context/auth-context";
import { useHttpClient } from "shared/hooks/http-hook";
import Tabs from "components/ui/tabs/Tabs";
import DailyKmComparison from "../km/DailyKmComparison";
import "./AlertCenter.css";

function shiftYmd(ymdValue, days) {
  const [y, m, d] = String(ymdValue || "").split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  return dt.toISOString().slice(0, 10);
}

function formatDuration(mins) {
  const m = Math.max(0, Math.round(Number(mins) || 0));
  if (m < 1) return "< 1 dk";
  if (m < 60) return `${m} dk`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} sa ${r} dk` : `${h} sa`;
}

function istanbulYmd(date) {
  return new Date(date).toLocaleDateString("en-CA", { timeZone: "Europe/Istanbul" });
}

const WEEKDAYS_TR = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];

function parseYmd(day) {
  const parts = String(day || "").split("-");
  if (parts.length !== 3) return null;
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if ([y, m, d].some((n) => Number.isNaN(n))) return null;
  return { y, m, d, mm: parts[1], dd: parts[2], yyyy: parts[0] };
}

function weekdayShort(day) {
  const p = parseYmd(day);
  if (!p) return "";
  return WEEKDAYS_TR[new Date(p.y, p.m - 1, p.d).getDay()] || "";
}

function formatDayShort(day) {
  const p = parseYmd(day);
  if (!p) return day || "";
  return `${p.mm}.${p.dd}`;
}

function formatDayLong(day) {
  const p = parseYmd(day);
  if (!p) return day || "";
  const wd = weekdayShort(day);
  return wd ? `${p.mm}.${p.dd}.${p.yyyy} ${wd}` : `${p.mm}.${p.dd}.${p.yyyy}`;
}

function isWeekendYmd(day) {
  const p = parseYmd(day);
  if (!p) return false;
  const dow = new Date(p.y, p.m - 1, p.d).getDay();
  return dow === 0 || dow === 6;
}

// Filo günlük dağılımı — 30 çubuk = o gün kaç farklı araçtan uyarı (sıfır günler dahil).
const DailyAlertChart = ({ byDay, vehicleTotal = 0, selectedDay, onSelectDay, selectedPlates = [] }) => {
  if (!Array.isArray(byDay) || byDay.length === 0) return null;

  const vehiclesOf = (d) => d.vehicles || 0;
  const max = byDay.reduce((m, d) => Math.max(m, vehiclesOf(d)), 0);
  const peak = byDay.reduce((best, d) => (vehiclesOf(d) > vehiclesOf(best) ? d : best), byDay[0]);
  const selected = selectedDay ? byDay.find((d) => d.day === selectedDay) : null;

  return (
    <div className="alert-center__byday">
      <div className="alert-center__byday-head">
        <h5 className="alert-center__byday-title">Günlük dağılım (kaç araçtan uyarı, son 30 gün)</h5>
        <span className="alert-center__byday-meta">
          Toplam {vehicleTotal} araç
          {peak && vehiclesOf(peak) > 0 ? ` · En yoğun ${formatDayLong(peak.day)} (${vehiclesOf(peak)})` : ""}
        </span>
      </div>
      <div className="alert-center__bars">
        {byDay.map((d, i) => {
          const vehicles = vehiclesOf(d);
          const alerts = d.count || 0;
          const h = max > 0 && vehicles > 0 ? Math.max(6, Math.round((vehicles / max) * 72)) : 3;
          const isToday = i === byDay.length - 1;
          const isSelected = selectedDay === d.day;
          const label = `${formatDayLong(d.day)} — ${vehicles} araç${alerts ? ` · ${alerts} uyarı` : ""}`;
          return (
            <button
              key={d.day}
              type="button"
              className={[
                "alert-center__bar-col",
                isWeekendYmd(d.day) ? "alert-center__bar-col--weekend" : "",
                isToday ? "alert-center__bar-col--today" : "",
                isSelected ? "alert-center__bar-col--selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              title={label}
              aria-label={label}
              aria-pressed={isSelected}
              onClick={() => onSelectDay(isSelected ? null : d.day)}
            >
              <span className="alert-center__bar-count">{vehicles > 0 ? vehicles : "\u00a0"}</span>
              <span
                className={`alert-center__bar ${vehicles === 0 ? "alert-center__bar--zero" : ""}`}
                style={{ height: h }}
              />
              <span className="alert-center__bar-label">
                <span>{formatDayShort(d.day)}</span>
                <span className="alert-center__bar-wd">{weekdayShort(d.day)}</span>
              </span>
            </button>
          );
        })}
      </div>
      {selected && (
        <div className="alert-center__byday-detail">
          <div className="alert-center__byday-detail-top">
            <div className="alert-center__byday-detail-summary">
              <strong>{formatDayLong(selected.day)}</strong>
              {`: ${vehiclesOf(selected)} araç`}
              {selected.count ? ` · ${selected.count} uyarı` : ""}
              {vehiclesOf(selected) > 0 ? (
                <span className="alert-center__byday-detail-hint"> — tabloda sarı vurgulandı</span>
              ) : null}
            </div>
            <button type="button" className="alert-center__byday-close" onClick={() => onSelectDay(null)}>
              Kapat
            </button>
          </div>
          {selectedPlates.length > 0 ? (
            <div className="alert-center__byday-plates">{selectedPlates.join(", ")}</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

// İlk yükleme: grafik + tablo yerleşimini taklit eder (spinner + boş alan yok).
const SKELETON_BAR_HEIGHTS = [
  28, 46, 38, 60, 42, 24, 18, 50, 64, 52, 36, 44, 72, 56, 40, 26, 48, 62, 54, 32, 22, 46, 68, 50, 34, 28, 56, 42, 38, 50,
];

const AlertsPanelSkeleton = ({ isServiceless }) => (
  <div className="alert-center__section" aria-busy="true" aria-live="polite" aria-label="Yükleniyor">
    <div className="alert-center__panel-head">
      <div className="alert-center__skel-line alert-center__skel-line--title" />
    </div>
    <div className="alert-center__byday">
      <div className="alert-center__byday-head">
        <div className="alert-center__skel-line" style={{ width: 260 }} />
        <div className="alert-center__skel-line" style={{ width: 150 }} />
      </div>
      <div className="alert-center__bars">
        {SKELETON_BAR_HEIGHTS.map((h, i) => (
          <div key={i} className="alert-center__skel-bar-col">
            <div className="alert-center__skel-bar" style={{ height: h }} />
          </div>
        ))}
      </div>
    </div>
    <div className="alert-center__skel-table">
      <div className="alert-center__skel-row alert-center__skel-row--head">
        <div className="alert-center__skel-cell alert-center__skel-cell--plate" />
        <div className="alert-center__skel-cell" />
        <div className="alert-center__skel-cell" />
        <div className="alert-center__skel-cell" />
        <div className="alert-center__skel-cell" />
        {isServiceless ? <div className="alert-center__skel-cell" /> : null}
        <div className="alert-center__skel-cell alert-center__skel-cell--wide" />
      </div>
      {Array.from({ length: 8 }, (_, r) => (
        <div key={r} className="alert-center__skel-row">
          <div className="alert-center__skel-cell alert-center__skel-cell--plate" />
          <div className="alert-center__skel-cell" />
          <div className="alert-center__skel-cell" />
          <div className="alert-center__skel-cell" />
          <div className="alert-center__skel-cell" />
          {isServiceless ? <div className="alert-center__skel-cell" /> : null}
          <div className="alert-center__skel-cell alert-center__skel-cell--wide" />
        </div>
      ))}
    </div>
  </div>
);

function formatLoc(e, which) {
  const addr = which === "end" ? e.endAddress : e.address;
  const loc = which === "end" ? e.endLocation : e.location;
  if (addr) return addr;
  if (loc && loc.lat !== null && loc.lat !== undefined && loc.lng !== null && loc.lng !== undefined) {
    return `${loc.lat.toFixed(4)}, ${loc.lng.toFixed(4)}`;
  }
  return "—";
}

// Ortak uyarı paneli — hem servissiz kullanım hem aşırı hız için (yalnızca type farklı).
// Backend: GET /telemetry/alerts?type=SERVICELESS_USE|SPEED_ALARM (sabit son 30 gün).
const AlertsPanel = ({ type, emptyText, summaryTitle }) => {
  const { sendRequest } = useHttpClient();
  const [data, setData] = useState({ events: [], byVehicle: [], byDay: [], total: 0 });
  const [loading, setLoading] = useState(false);
  const [openVids, setOpenVids] = useState(() => new Set());
  const [sort, setSort] = useState({ key: "today", dir: "desc" });
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams({ type });
      const res = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/telemetry/alerts?${qs.toString()}`);
      setData({
        events: Array.isArray(res?.events) ? res.events : [],
        byVehicle: Array.isArray(res?.byVehicle) ? res.byVehicle : [],
        byDay: Array.isArray(res?.byDay) ? res.byDay : [],
        total: res?.total || 0,
      });
    } catch (err) {
      setData({ events: [], byVehicle: [], byDay: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [sendRequest, type]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  // Olayları araca göre grupla (backend occurredAt'e göre azalan sıralı döner).
  const eventsByVehicle = useMemo(() => {
    const m = new Map();
    for (const e of data.events) {
      const vid = String(e.vehicle?._id || e.vehicle || e.node);
      if (!m.has(vid)) m.set(vid, []);
      m.get(vid).push(e);
    }
    return m;
  }, [data.events]);

  const vidsOnSelectedDay = useMemo(() => {
    if (!selectedDay) return new Set();
    const s = new Set();
    for (const e of data.events) {
      if (istanbulYmd(e.occurredAt) === selectedDay) {
        s.add(String(e.vehicle?._id || e.vehicle || e.node));
      }
    }
    return s;
  }, [selectedDay, data.events]);

  const platesOnSelectedDay = useMemo(() => {
    if (!selectedDay) return [];
    const seen = new Set();
    const plates = [];
    for (const e of data.events) {
      if (istanbulYmd(e.occurredAt) !== selectedDay) continue;
      const vid = String(e.vehicle?._id || e.vehicle || e.node);
      if (seen.has(vid)) continue;
      seen.add(vid);
      plates.push(e.plate || e.vehicle?.plate || "—");
    }
    return plates;
  }, [selectedDay, data.events]);

  const isServiceless = type === "SERVICELESS_USE";

  const toggle = (vid) =>
    setOpenVids((prev) => {
      const next = new Set(prev);
      if (next.has(vid)) next.delete(vid);
      else next.add(vid);
      return next;
    });

  // Test kolaylığı: bu sekmedeki tüm uyarı geçmişini sil.
  // Şimdilik gizli (servissiz + hız ortak panel) — geri açmak için yorumu kaldır.
  // const handleDeleteAll = async () => {
  //   // eslint-disable-next-line no-alert
  //   if (!window.confirm("Bu sekmedeki tüm uyarı geçmişi silinecek. Emin misiniz?")) return;
  //   try {
  //     const qs = new URLSearchParams({ type });
  //     await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/telemetry/alerts?${qs.toString()}`, "DELETE", null, {
  //       "Content-Type": "application/json",
  //     });
  //     setOpenVids(new Set());
  //     setSelectedDay(null);
  //     fetchAlerts();
  //   } catch (err) {
  //     /* sessiz */
  //   }
  // };

  // Sütun başlığına tıklayınca sırala; aynı sütuna tekrar tıklama yönü çevirir.
  const toggleSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "plate" ? "asc" : "desc" },
    );

  const sortedVehicles = useMemo(() => {
    const arr = [...data.byVehicle];
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    arr.sort((a, b) => {
      if (key === "plate") {
        const c = String(a.plate || "").localeCompare(String(b.plate || ""), "tr");
        return c !== 0 ? mul * c : (b.last30 || 0) - (a.last30 || 0);
      }
      const av = key === "lastOccurredAt" ? (a.lastOccurredAt ? new Date(a.lastOccurredAt).getTime() : 0) : a[key] || 0;
      const bv = key === "lastOccurredAt" ? (b.lastOccurredAt ? new Date(b.lastOccurredAt).getTime() : 0) : b[key] || 0;
      if (av === bv) return (b.last30 || 0) - (a.last30 || 0); // eşitlikte 30g'ye göre
      return av < bv ? -mul : mul;
    });
    return arr;
  }, [data.byVehicle, sort]);

  // Sıralanabilir başlık (component değil; render fonksiyonu — remount sorunu olmaz).
  const sortTh = (label, key, isNum) => (
    <th
      className={`alert-center__th-sort ${isNum ? "alert-center__col-num" : ""}`}
      onClick={() => toggleSort(key)}
      title="Sıralamak için tıkla"
    >
      {label}
      <span className="alert-center__sort-ind">{sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}</span>
    </th>
  );

  if (loading && data.events.length === 0) {
    return <AlertsPanelSkeleton isServiceless={isServiceless} />;
  }

  if (data.byVehicle.length === 0) {
    return (
      <div className="alert-center__section">
        <h4 className="alert-center__subtitle">{summaryTitle}</h4>
        <div className="alert-center__empty">{emptyText}</div>
      </div>
    );
  }

  return (
    <div className="alert-center__section">
      <div className="alert-center__panel-head">
        <h4 className="alert-center__subtitle">{summaryTitle}</h4>
        {/*
        <button
          type="button"
          className="alert-center__delete-btn"
          onClick={handleDeleteAll}
          title="Test: geçmişi temizle"
        >
          Tümünü sil
        </button>
        */}
      </div>
      <DailyAlertChart
        byDay={data.byDay}
        vehicleTotal={data.byVehicle.length}
        selectedDay={selectedDay}
        onSelectDay={setSelectedDay}
        selectedPlates={platesOnSelectedDay}
      />
      <table className="alert-center__table alert-center__summary">
        <thead>
          <tr>
            <th className="alert-center__col-toggle"></th>
            {sortTh("Plaka", "plate", false)}
            {sortTh("Bugün", "today", true)}
            {sortTh("Son 3 gün", "last3", true)}
            {sortTh("Son 7 gün", "last7", true)}
            {sortTh("Son 30 gün", "last30", true)}
            {isServiceless && sortTh("SMS (30g)", "smsLast30", true)}
            {sortTh("Son Olay", "lastOccurredAt", false)}
          </tr>
        </thead>
        <tbody>
          {sortedVehicles.map((v) => {
            const isOpen = openVids.has(v.vehicleId);
            const events = eventsByVehicle.get(v.vehicleId) || [];
            return (
              <React.Fragment key={v.vehicleId}>
                <tr
                  className={`alert-center__vrow ${isOpen ? "alert-center__vrow--open" : ""} ${
                    vidsOnSelectedDay.has(v.vehicleId) ? "alert-center__vrow--on-day" : ""
                  }`}
                  onClick={() => toggle(v.vehicleId)}
                >
                  <td className="alert-center__col-toggle">
                    <ChevronRightIcon
                      className={`alert-center__chevron ${isOpen ? "alert-center__chevron--open" : ""}`}
                      fontSize="small"
                    />
                  </td>
                  <td className="alert-center__plate">{v.plate || "—"}</td>
                  <td className="alert-center__col-num">
                    {v.today > 0 ? <span className="alert-center__count-badge">{v.today}</span> : "—"}
                  </td>
                  <td className="alert-center__col-num">{v.last3 || "—"}</td>
                  <td className="alert-center__col-num">{v.last7 || "—"}</td>
                  <td className="alert-center__col-num">{v.last30 || "—"}</td>
                  {isServiceless && (
                    <td className="alert-center__col-num" title="Son 30 günde sürücüye gönderilen SMS bildirimi">
                      {v.smsLast30 > 0 ? v.smsLast30 : "—"}
                    </td>
                  )}
                  <td className="alert-center__muted">
                    {v.lastOccurredAt ? new Date(v.lastOccurredAt).toLocaleString("tr-TR") : "—"}
                  </td>
                </tr>

                {isOpen && (
                  <tr className="alert-center__detail-row">
                    <td colSpan={isServiceless ? 8 : 7}>
                      <div className="alert-center__vehicle-body">
                        <table className="alert-center__table alert-center__events">
                          {isServiceless ? (
                            <>
                              <thead>
                                <tr>
                                  <th>Başlangıç</th>
                                  <th>Bitiş</th>
                                  <th>Süre</th>
                                  <th>Başlangıç Konumu</th>
                                  <th>Bitiş Konumu</th>
                                </tr>
                              </thead>
                              <tbody>
                                {events.map((e) => (
                                  <tr key={e._id}>
                                    <td>{new Date(e.occurredAt).toLocaleString("tr-TR")}</td>
                                    <td>
                                      {e.endedAt ? new Date(e.endedAt).toLocaleString("tr-TR") : "— (devam ediyor)"}
                                    </td>
                                    <td>{formatDuration(e.durationMinutes)}</td>
                                    <td className="alert-center__addr" title={formatLoc(e, "start")}>
                                      {formatLoc(e, "start")}
                                    </td>
                                    <td className="alert-center__addr" title={formatLoc(e, "end")}>
                                      {formatLoc(e, "end")}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </>
                          ) : (
                            <>
                              <thead>
                                <tr>
                                  <th>Zaman</th>
                                  <th>Hız</th>
                                  <th>Konum</th>
                                </tr>
                              </thead>
                              <tbody>
                                {events.map((e) => (
                                  <tr key={e._id}>
                                    <td>{new Date(e.occurredAt).toLocaleString("tr-TR")}</td>
                                    <td>
                                      {e.speed !== null && e.speed !== undefined ? `${Math.round(e.speed)} km/h` : "—"}
                                    </td>
                                    <td className="alert-center__addr" title={formatLoc(e, "start")}>
                                      {formatLoc(e, "start")}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </>
                          )}
                        </table>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const AlertCenter = () => {
  const auth = useContext(AuthContext);

  // Km karşılaştırması: dünle biten 30 İstanbul günü. Bugün henüz kapanmadığı için aralıkta yok.
  const to = shiftYmd(istanbulYmd(new Date()), -1);
  const from = shiftYmd(to, -29);

  const arventoEnabled = auth.company?.arventoConfig?.enabled;

  if (!arventoEnabled) {
    return <div className="alert-center__empty">Bu şirket için telemetri (Arvento) aktif değil.</div>;
  }

  const tabs = [
    {
      id: "km",
      label: "Km Karşılaştırması",
      content: (
        <div className="alert-center__section alert-center__section--km">
          <DailyKmComparison from={from} to={to} />
        </div>
      ),
    },
    {
      id: "serviceless",
      label: "Servis Dışı Kullanım",
      content: (
        <AlertsPanel
          type="SERVICELESS_USE"
          summaryTitle="Servis Dışı Kullanım — Araç Bazlı Özet (son 30 gün)"
          emptyText="Son 30 günde servis dışı kullanım uyarısı yok. 🎉"
        />
      ),
    },
    {
      id: "speed",
      label: "Hız Uyarıları",
      content: (
        <AlertsPanel
          type="SPEED_ALARM"
          summaryTitle="Aşırı Hız — Araç Bazlı Özet (son 30 gün)"
          emptyText="Son 30 günde aşırı hız uyarısı yok. 🎉"
        />
      ),
    },
  ];

  return (
    <div className="alert-center">
      <div className="alert-center__header">
        <h2 className="alert-center__title">Uyarı Merkezi</h2>
      </div>
      <Tabs tabs={tabs} id="telemetry-alert-center-km" />
    </div>
  );
};

export { DailyAlertChart, AlertsPanelSkeleton };
export default AlertCenter;
